import { useScienceTutorSection } from './scienceTutorContext';
import { AlertTriangle, CheckCircle2, Plus, RotateCcw, TestTube, X, XCircle } from 'lucide-react';
import { useState } from 'react';

import {
  ION_CASES,
  ION_SAMPLES,
  ION_TESTS,
  OBSERVATION_COLOUR_LABELS,
  checkIonIdentification,
  runIonTests,
  testName,
  type IonId,
  type IonRun,
  type IonStepResult,
  type IonTestId,
  type ObservationColour,
} from './instrumentalAnalysis';

/** Schematic tints only. The text label is always shown beside the tube. */
const OBSERVATION_TINT: Record<ObservationColour, string> = {
  none: 'bg-transparent',
  white: 'bg-canvas-pure border-t-2 border-stone2',
  cream: 'bg-surface-soft border-t-2 border-stone2',
  yellow: 'bg-brand-sunshine',
  'pale-yellow': 'bg-wash-sunshine border-t-2 border-stone2',
  green: 'bg-brand-mint',
  'red-brown': 'bg-brand-coral',
  blue: 'bg-brand-sky/70',
  brown: 'bg-slate2',
  milky: 'bg-canvas-pure/90',
};

const KIND_LABEL: Record<IonStepResult['kind'], string> = {
  positive: 'Positive result',
  negative: 'No reaction',
  interference: 'Interference',
  stopped: 'Not run',
};

function TubeResult({ step, index }: { step: IonStepResult; index: number }) {
  const colourLabel =
    step.test === 'sodium-hydroxide' && step.kind === 'positive' && !step.precipitate
      ? 'No precipitate · litmus turns blue'
      : OBSERVATION_COLOUR_LABELS[step.colour];
  return (
    <li className="flex gap-4 rounded-2xl border border-hairline bg-white p-4">
      <div
        className="relative h-24 w-8 shrink-0 overflow-hidden rounded-b-full border-2 border-brand-sky/40 bg-wash-sky"
        role="img"
        aria-label={`Tube after step ${index + 1}: ${colourLabel}`}
      >
        {step.precipitate ? (
          <span className={`absolute inset-x-0 bottom-0 h-1/3 ${OBSERVATION_TINT[step.colour]}`} />
        ) : null}
        {step.colour === 'milky' ? (
          <span className="absolute inset-x-0 top-1/3 bottom-0 bg-canvas-pure/80" />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">
          Step {index + 1} · {testName(step.test)}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm font-extrabold text-ink">
          {KIND_LABEL[step.kind]}
          <span className="rounded-full bg-surface px-2 py-0.5 text-xs font-bold text-ink-soft">
            {colourLabel}
          </span>
        </p>
        <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">{step.observation}</p>
        {step.equation ? (
          <p className="mt-1 font-mono text-xs font-bold text-ink">{step.equation}</p>
        ) : null}
        {step.interference ? (
          <p className="mt-2 flex gap-2 rounded-xl bg-wash-sunshine p-3 text-xs font-bold leading-5 text-ink-soft">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {step.interference}
          </p>
        ) : null}
      </div>
    </li>
  );
}

export function IonsMode() {
  const [sampleId, setSampleId] = useState(ION_SAMPLES[0].id);
  const [plan, setPlan] = useState<IonTestId[]>([]);
  const [run, setRun] = useState<IonRun | null>(null);
  const [guess, setGuess] = useState<IonId | ''>('');
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('IonsMode', { sampleId, plan, run, guess, checked });

  const sample = ION_SAMPLES.find((candidate) => candidate.id === sampleId) ?? ION_SAMPLES[0];
  const identification =
    checked && guess && run ? checkIonIdentification(sample, guess, run) : null;

  const freshPortion = () => {
    setPlan([]);
    setRun(null);
    setChecked(false);
  };

  const chooseSample = (id: string) => {
    setSampleId(id);
    setGuess('');
    freshPortion();
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          Unknown samples
        </p>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {ION_SAMPLES.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              aria-pressed={candidate.id === sample.id}
              onClick={() => chooseSample(candidate.id)}
              className={`rounded-2xl border px-3 py-2 text-sm font-extrabold transition-colors ${
                candidate.id === sample.id
                  ? 'border-brand-sky bg-wash-sky text-ink'
                  : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
              }`}
            >
              {candidate.label}
            </button>
          ))}
        </div>
        <p className="mt-5 rounded-2xl bg-wash-sunshine p-4 text-sm font-medium leading-6 text-ink-soft">
          Each sample contains one of nine ions. Plan the order of tests on one portion. Reagents
          stay in the tube, so the order can change what you see.
        </p>
      </aside>

      <article className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          {sample.label} · plan your tests
        </p>
        <h2 className="mt-2 text-2xl font-extrabold text-ink">Which ion is in {sample.label}?</h2>
        <div className="mt-5 grid gap-2 sm:grid-cols-2" role="group" aria-label="Add a test">
          {ION_TESTS.map((test) => (
            <button
              key={test.id}
              type="button"
              disabled={plan.includes(test.id) || run !== null}
              onClick={() => setPlan((current) => [...current, test.id])}
              className="flex items-start gap-2 rounded-2xl border border-hairline bg-white px-4 py-3 text-left text-sm transition-colors hover:border-brand-sky/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="mt-0.5 h-4 w-4 shrink-0 text-brand-sky" aria-hidden="true" />
              <span>
                <span className="block font-extrabold text-ink">
                  Add {test.name.toLowerCase()} · {test.reagent}
                </span>
                <span className="block text-xs font-medium text-slate2">{test.method}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="mt-5 rounded-2xl border border-hairline bg-surface p-4">
          <p className="text-sm font-extrabold text-ink">Test order</p>
          {plan.length === 0 ? (
            <p className="mt-1 text-sm font-medium text-slate2">No tests chosen yet.</p>
          ) : (
            <ol className="mt-2 grid gap-2">
              {plan.map((test, index) => (
                <li key={test} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 text-sm font-bold text-ink">
                  <span>
                    {index + 1}. {testName(test)}
                  </span>
                  {run === null ? (
                    <button
                      type="button"
                      aria-label={`Remove ${testName(test)}`}
                      onClick={() => setPlan((current) => current.filter((item) => item !== test))}
                      className="rounded-full p-1 text-slate2 hover:bg-surface hover:text-ink"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={plan.length === 0 || run !== null}
              onClick={() => setRun(runIonTests(sample.ionId, plan))}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <TestTube className="h-4 w-4" aria-hidden="true" /> Run the tests
            </button>
            <button
              type="button"
              onClick={freshPortion}
              className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink-soft hover:bg-white"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Fresh portion
            </button>
          </div>
        </div>

        {run && !run.ok ? (
          <p role="alert" className="mt-5 rounded-2xl border border-brand-coral/45 bg-wash-coral p-4 text-sm font-bold text-ink">
            {run.error}
          </p>
        ) : null}
        {run?.ok ? (
          <ol className="mt-5 grid gap-3" aria-label="Observations">
            {run.steps.map((step, index) => (
              <TubeResult key={step.test} step={step} index={index} />
            ))}
          </ol>
        ) : null}

        {run?.ok ? (
          <section className="mt-6" aria-labelledby="identify-ion-heading">
            <h3 id="identify-ion-heading" className="text-lg font-extrabold text-ink">
              Identify the ion
            </h3>
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5" role="group" aria-label="Choose the ion">
              {ION_CASES.map((ion) => (
                <button
                  key={ion.id}
                  type="button"
                  aria-pressed={guess === ion.id}
                  aria-label={`${ion.name} ${ion.symbol}`}
                  onClick={() => {
                    setGuess(ion.id);
                    setChecked(false);
                  }}
                  className={`rounded-2xl border px-3 py-2 text-sm font-extrabold transition-colors ${
                    guess === ion.id
                      ? 'border-brand-coral bg-wash-coral text-ink'
                      : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
                  }`}
                >
                  {ion.symbol}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={!guess}
              onClick={() => setChecked(true)}
              className="mt-4 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Check identification
            </button>
            {identification ? (
              <div
                role="status"
                className={`mt-4 rounded-2xl border p-4 ${
                  identification.correct
                    ? 'border-brand-mint/45 bg-wash-mint'
                    : 'border-brand-coral/45 bg-wash-coral'
                }`}
              >
                <p className="flex items-center gap-2 font-extrabold text-ink">
                  {identification.correct ? (
                    <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
                  ) : (
                    <XCircle className="h-5 w-5 text-brand-coral" aria-hidden="true" />
                  )}
                  {identification.correct ? 'Correct ion' : 'Not this ion'}
                </p>
                <p className="mt-2 text-sm font-medium leading-6 text-ink-soft">
                  {identification.feedback}
                </p>
              </div>
            ) : null}
          </section>
        ) : null}
      </article>
    </div>
  );
}
